import { useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import type { AppointmentCategory } from "../../shared/schema";

export interface UseCalendarEventsOptions {
  limit?: number;
  /** @deprecated Use `category` instead */
  onlyUpcoming?: boolean;
  /** Filter by appointment category */
  category?: AppointmentCategory;
  /** Whether to enable the query (defaults to true) */
  enabled?: boolean;
}

// 12 hours in milliseconds for "upcoming" leeway
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

export function useCalendarEvents(options: UseCalendarEventsOptions = {}) {
  const { limit = 50, onlyUpcoming, category = "all", enabled = true } = options;

  const query = useTellescopeInfiniteQuery({
    model: "calendar_events",
    method: "getSome",
    request: {
      filter: {},
      limit,
    },
    select: (data) => data.pages.flat(),
    enabled,
  });

  const allEvents = query.data ?? [];

  // Determine which category to use (support legacy onlyUpcoming prop)
  const effectiveCategory = onlyUpcoming ? "upcoming" : category;

  // Filter events based on category
  const now = Date.now();
  const upcomingThreshold = now - TWELVE_HOURS_MS; // 12 hours ago

  const filteredEvents = allEvents.filter((event) => {
    // Cancelled events are now shown (with cancelled indicator)
    // so users can see them and access reschedule links if configured

    switch (effectiveCategory) {
      case "upcoming":
        // Upcoming: start time is >= 12 hours ago (gives some leeway for recent past)
        return event.startTimeInMS >= upcomingThreshold;
      case "past":
        // Past: start time is < 12 hours ago
        return event.startTimeInMS < upcomingThreshold;
      case "all":
      default:
        return true;
    }
  });

  // Sort by start time (descending) - future appointments first, then past
  const sortedEvents = [...filteredEvents].sort((a, b) => b.startTimeInMS - a.startTimeInMS);

  return {
    ...query,
    events: sortedEvents,
  };
}
